import React, { useEffect, useMemo, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as Actions from '../redux/actions';
import InfoCard from './InfoCard';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import BadgeIcon from '@mui/icons-material/Badge';
import AddIcon from '@mui/icons-material/Add';
import DoneIcon from '@mui/icons-material/Done';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import LockResetIcon from '@mui/icons-material/LockReset';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

const EMPTY_FORM = { id: 'new', first_name: '', last_name: '', username: '', email: '' };

const fullName = (op) => `${op.first_name || ''} ${op.last_name || ''}`.trim();

const byName = (a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`);

const OperatorsCard = ({
  customerId, authToken, users, reportTemplates,
  fetchUsers, createUser, updateUser, setUserPassword, deactivateUser, reactivateUser, fetchReportTemplates,
}) => {
  const [loaded, setLoaded] = useState(null); // null while loading, false if this user can't manage the account
  const [form, setForm] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [menu, setMenu] = useState(null); // { anchor, op } for the open row menu

  useEffect(() => {
    setLoaded(null);
    setForm(null);
    setError(null);
    setNotice(null);
    fetchUsers(authToken, 'operators', { customer_id: customerId })
      .then(() => setLoaded(true))
      .catch(() => setLoaded(false));
    fetchReportTemplates(customerId, authToken).catch(() => {});
  }, [customerId, authToken, fetchUsers, fetchReportTemplates]);

  // Both come from the store, so edits on the Admin dashboard or the Report
  // templates tab of the Reports card show up here without a reload.
  const operators = useMemo(() => (
    Object.values(users || {})
      .filter((u) => u.type === 'operator' && `${u.customer?.id}` === `${customerId}`)
      .sort(byName)
  ), [users, customerId]);

  const templates = useMemo(() => (
    Object.values(reportTemplates || {})
      .filter((t) => `${t.customer_id}` === `${customerId}`)
      .sort((a, b) => a.name.localeCompare(b.name))
  ), [reportTemplates, customerId]);

  // Closes the row menu, then runs the chosen action on that row's operator.
  const fromMenu = (action) => {
    if (!menu) return; // a second click after the menu has already closed
    const { op } = menu;
    setMenu(null);
    action(op);
  };

  const assignTemplate = (op, templateId) => {
    run(op.id, updateUser({ id: op.id, report_template_id: templateId || null }, authToken));
  };

  // Tracks which row is busy and surfaces the server's error for a request.
  const run = (id, request, onSuccess) => {
    setBusyId(id);
    setError(null);
    setNotice(null);
    return request
      .then(onSuccess)
      .catch((e) => setError(e.message))
      .finally(() => setBusyId(null));
  };

  const saveForm = () => {
    const fields = {
      first_name: form.first_name,
      last_name: form.last_name,
      username: form.username,
      email: form.email,
    };

    if (form.id === 'new') {
      const operator = {
        type: 'operator', customer_id: customerId, ...fields,
        generate_password: true, temppass: true, send_invite: true,
      };
      run('new', createUser(operator, authToken), (json) => {
        setForm(null);
        setNotice(`Invite emailed to ${json.user.email} with a temporary password.`);
      });
    } else {
      run(form.id, updateUser({ id: form.id, ...fields }, authToken), () => setForm(null));
    }
  };

  const resetPassword = (op) => {
    if (!window.confirm(`Email ${fullName(op)} a new temporary password? Their current password will stop working.`)) return;
    const reset = { id: op.id, generate_password: true, temppass: true, send_email: true };
    run(op.id, setUserPassword(reset, authToken), () => setNotice(`New temporary password emailed to ${op.email}.`));
  };

  const toggleActive = (op) => {
    if (!op.deactivated_at && !window.confirm(`Deactivate ${fullName(op)}? They will be signed out and unable to log in.`)) return;
    run(op.id, op.deactivated_at ? reactivateUser(op.id, authToken) : deactivateUser(op.id, authToken));
  };

  // Hidden until the list loads; a failed load means this user can't manage this account.
  if (!loaded) return null;

  const active = operators.filter((o) => !o.deactivated_at);
  const rows = form?.id === 'new' ? [...operators, form] : operators;

  const input = (field, placeholder) => (
    <input
      className="contact-input"
      placeholder={placeholder}
      value={form[field]}
      autoCapitalize={field === 'username' || field === 'email' ? 'none' : undefined}
      onChange={(e) => setForm({ ...form, [field]: e.target.value })}
    />
  );

  return (
    <InfoCard
      id="account-operators"
      icon={<BadgeIcon />}
      title="Operators"
      count={active.length}
      subtitle="Customer logins for recording readings"
      summary={active.length ? active.map(fullName).join(', ') : 'No operator accounts'}
    >
      {notice && <div className="operator-notice">{notice}</div>}
      {error && <div className="operator-error">{error}</div>}

      {rows.length > 0 && (
        <TableContainer component={Paper} className="contact-table-container">
          <Table className="contact-table" aria-label="account operators" size="medium">
            <TableHead>
              <TableRow>
                <TableCell>Operator</TableCell>
                <TableCell>Username / Email</TableCell>
                <TableCell>Report template</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((op) => {
                const isEditing = form?.id === op.id;
                const busy = busyId === op.id;

                return (
                  <TableRow
                    key={`operator-${op.id}`}
                    className={[
                      isEditing ? 'contact-row-editing' : '',
                      op.deactivated_at ? 'operator-row-deactivated' : '',
                    ].filter(Boolean).join(' ')}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>
                      {isEditing ? (
                        <div className="contact-edit-fields">
                          {input('first_name', 'First name')}
                          {input('last_name', 'Last name')}
                        </div>
                      ) : (
                        <>
                          <div className="contact-cell-primary">
                            {fullName(op) || '—'}
                            {op.deactivated_at ? (
                              <span className="contact-primary-badge operator-badge-inactive">Deactivated</span>
                            ) : op.temppass ? (
                              <span className="contact-primary-badge">Awaiting first login</span>
                            ) : null}
                          </div>
                        </>
                      )}
                    </TableCell>

                    <TableCell>
                      {isEditing ? (
                        <div className="contact-edit-fields">
                          {input('username', 'Username')}
                          {input('email', 'Email')}
                        </div>
                      ) : (
                        <>
                          <div className="contact-cell-primary">{op.username}</div>
                          <div className="contact-cell-secondary">
                            <a href={`mailto:${op.email}`} className="contact-email-link">{op.email}</a>
                          </div>
                        </>
                      )}
                    </TableCell>

                    <TableCell>
                      {op.id === 'new' ? (
                        <span className="contact-cell-secondary">Assign after creating</span>
                      ) : (
                        <select
                          className={`contact-input operator-template-select browser-default${op.report_template_id ? '' : ' is-unassigned'}`}
                          value={op.report_template_id || ''}
                          disabled={busy || isEditing}
                          aria-label={`Report template for ${fullName(op)}`}
                          onChange={(e) => assignTemplate(op, e.target.value)}
                        >
                          <option value="">No template (can't start reports)</option>
                          {templates.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      )}
                    </TableCell>

                    <TableCell align="right">
                      {isEditing ? (
                        <div className="contact-row-actions">
                          <div className="contact-btn-wrap">
                            {busy && <div className="saving-flash shown" aria-hidden="true" />}
                            <button
                              type="button"
                              className="contact-btn contact-btn-primary"
                              disabled={busy}
                              onClick={saveForm}
                            >
                              <DoneIcon />
                              <span>{busy ? 'Saving...' : op.id === 'new' ? 'Send invite' : 'Save'}</span>
                            </button>
                          </div>
                          <button
                            type="button"
                            className="contact-btn"
                            disabled={busy}
                            onClick={() => { setForm(null); setError(null); }}
                          >
                            <CloseIcon />
                            <span>Cancel</span>
                          </button>
                        </div>
                      ) : (
                        <div className="contact-row-actions">
                          <div className="contact-btn-wrap">
                            {busy && <div className="saving-flash shown" aria-hidden="true" />}
                            <button
                              type="button"
                              className="operator-menu-btn"
                              aria-label={`Actions for ${fullName(op)}`}
                              aria-haspopup="menu"
                              disabled={!!form || busy}
                              onClick={(e) => setMenu({ anchor: e.currentTarget, op })}
                            >
                              <MoreHorizIcon />
                            </button>
                          </div>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu
        anchorEl={menu?.anchor}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        className="operator-menu"
      >
        <MenuItem onClick={() => fromMenu((op) => { setForm({ ...op }); setError(null); setNotice(null); })}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        {menu && !menu.op.deactivated_at && (
          <MenuItem onClick={() => fromMenu(resetPassword)}>
            <ListItemIcon><LockResetIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Reset password</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          className={menu?.op.deactivated_at ? '' : 'operator-menu-danger'}
          onClick={() => fromMenu(toggleActive)}
        >
          <ListItemIcon>{menu?.op.deactivated_at ? <RestoreIcon fontSize="small" /> : <BlockIcon fontSize="small" />}</ListItemIcon>
          <ListItemText>{menu?.op.deactivated_at ? 'Reactivate' : 'Deactivate'}</ListItemText>
        </MenuItem>
      </Menu>

      {!form && (
        <div className="add-new" onClick={() => { setForm({ ...EMPTY_FORM }); setError(null); setNotice(null); }}>
          <AddIcon />
          <span>Add operator</span>
        </div>
      )}
    </InfoCard>
  );
};

function mapStateToProps(state) {
  return {
    authToken: state.authToken,
    users: state.users,
    reportTemplates: state.reportTemplates,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(OperatorsCard);
